import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Calendar, MapPin, Users, Clock, Euro } from 'lucide-react';
import EventRegistrationModal from '../EventRegistrationModal';
import PhotoGallery from '../PhotoGallery';
import EventImageCarousel from '../EventImageCarousel';
import { OptimizedImage } from '../ui/optimized-image';

const EventsManagement = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' ou 'archive'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [eventToRegister, setEventToRegister] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userName, setUserName] = useState(null);

  useEffect(() => {
    // Récupérer l'email et le nom de l'utilisateur
    const email = localStorage.getItem('memberEmail');
    setUserEmail(email);
    
    // Récupérer le nom complet (depuis memberFullName ou currentUser)
    const fullName = localStorage.getItem('memberFullName');
    if (fullName) {
      setUserName(fullName);
    } else {
      // Fallback: essayer de récupérer depuis currentUser
      try {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        if (currentUser.first_name || currentUser.last_name) {
          setUserName(`${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim());
        }
      } catch (e) {
        console.error('Erreur lecture currentUser:', e);
      }
    }
    
    fetchEvents();
    checkPaymentReturn();
  }, []);

  // Vérifier si on revient de Stripe
  const checkPaymentReturn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const eventSuccess = urlParams.get('event_success');

    console.log('🔍 Vérification retour Stripe (membre):', { sessionId, eventSuccess });

    if (sessionId && eventSuccess) {
      // Nettoyer les accolades si présentes
      const cleanSessionId = sessionId.replace(/[{}]/g, '');
      console.log('🔄 Session ID nettoyé:', cleanSessionId);
      
      await pollPaymentStatus(cleanSessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10; // Plus de tentatives mais plus rapides
    
    if (attempts >= maxAttempts) {
      alert('Vérification du paiement en cours... Veuillez vérifier votre email.');
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    try {
      console.log(`🔄 Tentative ${attempts + 1}/${maxAttempts} (membre)`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/registration/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          alert('✅ ' + data.message);
          window.history.replaceState({}, document.title, window.location.pathname);
          window.location.reload();
          return;
        }
      }
      
      // Continuer le polling avec délai réduit à 500ms
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 500);
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
      // Réessayer même en cas d'erreur
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 500);
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('memberToken');
      // Récupérer TOUS les événements (passés et futurs) pour pouvoir les séparer
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/events?upcoming_only=false`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const allEvents = data.events || [];
        
        // Séparer les événements à venir et passés
        const now = new Date();
        const upcoming = allEvents.filter(event => new Date(event.end_datetime) >= now);
        const past = allEvents.filter(event => new Date(event.end_datetime) < now);
        
        // Trier par date
        upcoming.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
        past.sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime)); // Plus récents en premier
        
        setUpcomingEvents(upcoming);
        setPastEvents(past);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des événements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formater la date SANS conversion de timezone
  // On extrait directement les valeurs du string ISO
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Extraire la date directement du string ISO
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      // Créer une date à partir des composants pour le formatage
      // On utilise UTC pour éviter les décalages
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC'
      });
    }
    return '';
  };

  // Formater l'heure SANS conversion de timezone
  const formatTime = (dateString) => {
    if (!dateString) return '';
    // Extraire l'heure directement du string ISO
    const match = dateString.match(/T(\d{2}):(\d{2})/);
    if (match) {
      const [, hours, minutes] = match;
      return `${hours}:${minutes}`;
    }
    return '';
  };

  const getEventTypeLabel = (type) => {
    const types = {
      'assembly': 'Assemblée',
      'community': 'Communautaire',
      'cultural': 'Culturel',
      'other': 'Autre'
    };
    return types[type] || 'Événement';
  };

  const getEventTypeColor = (type) => {
    const colors = {
      'assembly': 'bg-blue-100 text-blue-800',
      'community': 'bg-green-100 text-green-800',
      'cultural': 'bg-purple-100 text-purple-800',
      'other': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" 
             style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div>
      </div>
    );
  }

  // Vue détail d'un événement
  if (selectedEvent) {
    return (
      <div className="space-y-6">
        <Button
          variant="outline"
          onClick={() => setSelectedEvent(null)}
          className="mb-4"
          style={{ borderColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' }}
        >
          ← Retour aux événements
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle 
                className="text-3xl"
                style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
              >
                {selectedEvent.title}
              </CardTitle>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getEventTypeColor(selectedEvent.event_type)}`}>
                {getEventTypeLabel(selectedEvent.event_type)}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Image de couverture / Carrousel si album photo */}
            <EventImageCarousel 
              coverImage={selectedEvent.cover_image_url || selectedEvent.image_url}
              photoAlbum={selectedEvent.photo_album}
              title={selectedEvent.title}
              height={500}
            />
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center text-gray-600">
                <Calendar className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                <span>{formatDate(selectedEvent.start_datetime)}</span>
              </div>
              
              <div className="flex items-center text-gray-600">
                <Clock className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                <span>{formatTime(selectedEvent.start_datetime)} - {formatTime(selectedEvent.end_datetime)}</span>
              </div>
              
              {selectedEvent.location && (
                <div className="flex items-center text-gray-600">
                  <MapPin className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                  <span>{selectedEvent.location}</span>
                </div>
              )}
              
              {selectedEvent.max_attendees && (
                <div className="flex items-center text-gray-600">
                  <Users className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                  <span>{selectedEvent.max_attendees} places</span>
                </div>
              )}
            </div>

            {selectedEvent.tariffs && selectedEvent.tariffs.length > 0 ? (
              <div className="mb-6">
                <h4 className="font-semibold mb-2" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                  Tarifs
                </h4>
                <div className="space-y-2">
                  {selectedEvent.tariffs.map((tariff, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span style={{ fontFamily: '"Lora", serif' }}>{tariff.label}</span>
                      <span 
                        className="font-bold"
                        style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                      >
                        {tariff.price.toFixed(2)} €
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : selectedEvent.price > 0 && (
              <div className="flex items-center text-lg font-semibold" style={{ color: 'rgb(196, 150, 96)' }}>
                <Euro className="w-5 h-5 mr-2" />
                <span>{selectedEvent.price.toFixed(2)} €</span>
              </div>
            )}
            
            <div 
              className="prose prose-lg max-w-none mt-6"
              style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              dangerouslySetInnerHTML={{ __html: selectedEvent.description }}
            />

            {/* Bouton d'inscription - N'afficher que si l'événement a des tarifs configurés (même gratuits) */}
            {((selectedEvent.tariffs && selectedEvent.tariffs.length > 0) || (selectedEvent.price !== undefined && selectedEvent.price !== null)) && (
              <div className="pt-6">
                <Button
                  size="lg"
                  className="w-full text-white"
                  onClick={() => {
                    setEventToRegister(selectedEvent);
                    setShowRegistrationModal(true);
                    setSelectedEvent(null);
                  }}
                  style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                >
                  S'inscrire à cet événement
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Vue liste des événements
  const currentEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 
          className="text-3xl font-bold"
          style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
        >
          Événements
        </h2>
      </div>

      {/* Onglets */}
      <div className="flex justify-center border-b border-gray-200">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'upcoming'
              ? 'border-b-2 text-amber-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'upcoming' ? { borderBottomColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' } : {}}
        >
          Événements à venir
        </button>
        <button
          onClick={() => setActiveTab('archive')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'archive'
              ? 'border-b-2 text-amber-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'archive' ? { borderBottomColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' } : {}}
        >
          Archives
        </button>
      </div>

      {currentEvents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500" style={{ fontFamily: '"Lora", serif' }}>
              {activeTab === 'upcoming' 
                ? "Aucun événement à venir pour le moment."
                : "Aucun événement passé."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {currentEvents.map((event) => {
            const coverImage = event.cover_image_url || event.image_url;
            return (
            <Card key={event.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-6">
                  {coverImage && (
                    <OptimizedImage 
                      src={coverImage} 
                      alt={event.title}
                      className="w-48 h-32 object-cover rounded-lg flex-shrink-0"
                      useThumbnail={true}
                      placeholder="skeleton"
                    />
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 
                        className="text-2xl font-bold flex-1"
                        style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                      >
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getEventTypeColor(event.event_type)}`}>
                          {getEventTypeLabel(event.event_type)}
                        </span>
                        {/* Badge places restantes */}
                        {typeof event.places_remaining === 'number' && (
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            event.places_remaining === 0 
                              ? 'bg-red-100 text-red-800' 
                              : event.places_remaining <= 10 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-green-100 text-green-800'
                          }`}>
                            {event.places_remaining === 0 
                              ? 'Complet' 
                              : `${event.places_remaining} place${event.places_remaining > 1 ? 's' : ''}`}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-4 h-4 mr-2" />
                        <span>{formatDate(event.start_datetime)}</span>
                      </div>
                      
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>{formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</span>
                      </div>
                      
                      {event.location && (
                        <div className="flex items-center text-gray-600">
                          <MapPin className="w-4 h-4 mr-2" />
                          <span>{event.location}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      {event.tariffs && event.tariffs.length > 0 ? (
                        <div className="flex items-center font-semibold" style={{ color: 'rgb(196, 150, 96)' }}>
                          <Euro className="w-4 h-4 mr-1" />
                          <span>À partir de {Math.min(...event.tariffs.map(t => t.price)).toFixed(2)} €</span>
                        </div>
                      ) : event.price > 0 && (
                        <div className="flex items-center font-semibold" style={{ color: 'rgb(196, 150, 96)' }}>
                          <Euro className="w-4 h-4 mr-1" />
                          <span>{event.price.toFixed(2)} €</span>
                        </div>
                      )}
                      
                      <Button
                        variant="outline"
                        onClick={() => setSelectedEvent(event)}
                        style={{ 
                          borderColor: 'rgb(196, 150, 96)', 
                          color: 'rgb(196, 150, 96)',
                          fontFamily: '"Lora", serif'
                        }}
                      >
                        Voir les détails
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      )}

      {/* Modal d'inscription */}
      {showRegistrationModal && eventToRegister && (
        <EventRegistrationModal
          event={eventToRegister}
          isPublic={true}  // Dans l'espace membre, toujours autoriser l'inscription (le membre est déjà connecté)
          userEmail={userEmail}
          userName={userName}
          onClose={() => {
            setShowRegistrationModal(false);
            setEventToRegister(null);
          }}
        />
      )}
    </div>
  );
};

export default EventsManagement;
